/* ============================================================
   SAM-1073 — New service, four steps (canvas 21 oct, approved): Kind · Details · Look · Booking &
   price. Kind is one screen with every setup; Details is name + description (EN | ES);
   Look is optional (cover photo, icon, card color, with the app preview); Booking & price is the
   few things a service needs to be bookable, with Free to book on or off. "Create service" saves it
   and the full editor opens on the new service (seasons, resources, add-ons live there).
   State is the editor's own (`useServiceEditor`), so the payload is exactly what the editor saves.
   ============================================================ */
import React, { useRef, useState } from 'react';
import {
  ArrowLeft, Check, Sparkles, Clock, Building2, Ticket, User, Users, Flag, Box, Armchair, Sun, Palmtree, Coffee,
  Calendar, CalendarDays,
} from 'lucide-react';
import { S3ImageUpload } from '../../../components/S3ImageUpload';
import { OfferPickerCard } from '../../../components/offer/OfferPickerGrid';
import { ServiceLivePreview } from '../ServiceLivePreview';
import { BadgeField, type Editor } from '../ServiceEditorPage';
import { applyMarkdownEdit, type MarkdownKind } from '../../../utils/markdownEdit';
import { pricingCurrency, resourceWord } from '../editorV2';
import { CREATE_SERVICE_SUBTYPES, CREATE_SERVICE_TYPES, type CreateServiceType } from './serviceCreateTaxonomy';
import type { useCreateServiceFlow } from './CreateServiceFlow';
import {
  WIZARD_STEPS, stepIndex, stepBlocker, isFreeForm, setFree, setPrice, priceOf, setCurrency, hasSlots, hasStartTimes,
  pricedByEventSize, sampleStartTimes, startStepMinutes, type WizardStep,
} from './wizardModel';

type Flow = ReturnType<typeof useCreateServiceFlow>;

const SUBTYPE_ICONS: Record<string, React.ComponentType<any>> = {
  private: User, class: Users, teetime: Flag, stay: Building2, equipment: Box, tables: Armchair, daypass: Sun,
  cabana: Palmtree, minconsumo: Coffee, single: Calendar, multiday: CalendarDays,
};
const TYPE_ICONS: Record<CreateServiceType, React.ComponentType<any>> = { appointment: Clock, rental: Building2, ticket: Ticket };
const TYPE_SUB: Record<CreateServiceType, string> = {
  appointment: 'Booked by time with a person.',
  rental: 'A thing or place for a time.',
  ticket: 'Entry for a day or an event.',
};
const CARD_COLORS = ['#D4D4D4', '#005488', '#0E8345', '#D98A00', '#B3261E', '#6B4FA0'];
const ink = (n: 900 | 700 | 500) => ({ color: `var(--ink-${n})` });
const brand = 'var(--tenant-primary, #005488)';

const Label: React.FC<{ text: string; required?: boolean; children: React.ReactNode }> = ({ text, required, children }) => (
  <label className="flex flex-col gap-1.5 text-[13px] font-semibold min-w-0" style={ink(700)}>
    <span>{text}{required && <span style={{ color: brand }}> *</span>}</span>
    {children}
  </label>
);

/** A number with its unit after it ("60 minutes"), as in the mock. */
const UnitInput: React.FC<{ label: string; unit: string; value: string; onChange: (v: string) => void; required?: boolean }> = (
  { label, unit, value, onChange, required },
) => (
  <Label text={label} required={required}>
    <span className="flex items-center gap-2 h-11 px-3 rounded-[8px] border border-solid border-[var(--line)] bg-[var(--surface)] font-normal">
      <input
        inputMode="numeric"
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/[^\d]/g, ''))}
        className="grow min-w-0 bg-transparent border-0 outline-none text-[14px]"
        style={ink(900)}
      />
      <span className="shrink-0 text-[14px] font-medium" style={ink(500)}>{unit}</span>
    </span>
  </Label>
);

const Card: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="rounded-[12px] bg-white p-5 sm:p-6 flex flex-col gap-4" style={{ border: '1px solid var(--line)' }}>
    <h2 className="m-0 text-[17px] font-bold" style={ink(900)}>{title}</h2>
    {children}
  </section>
);

const SegButton: React.FC<{ on: boolean; onClick: () => void; children: React.ReactNode }> = ({ on, onClick, children }) => (
  <button
    type="button"
    aria-pressed={on}
    onClick={onClick}
    className="h-10 px-4 rounded-[10px] text-[14px] font-semibold cursor-pointer"
    style={{
      border: `1px solid ${on ? brand : 'var(--line)'}`,
      background: on ? `color-mix(in srgb, ${brand} 10%, white)` : '#fff',
      color: on ? brand : 'var(--ink-700)',
    }}
  >
    {children}
  </button>
);

// --- Step 1 · Kind -----------------------------------------------------------------------------

const KindStep: React.FC<{ editor: Editor; flow: Flow; businesses: Array<{ id: string | number; name?: string }>; canPickBusiness: boolean }> = (
  { editor, flow, businesses, canPickBusiness },
) => (
  <div className="flex flex-col gap-6">
    <div>
      <h1 className="m-0 text-[24px] font-bold" style={ink(900)}>What kind of service is it?</h1>
      <p className="m-0 mt-1.5 text-[15px]" style={ink(500)}>
        Pick the closest one. It presets schedule, price and policies. You can change it later from the editor.
      </p>
    </div>
    {canPickBusiness && !editor.formData.business_id && (
      <Label text="Which business is this service for?" required>
        <select
          className="h-11 px-3 rounded-[8px] border border-solid border-[var(--line)] bg-[var(--surface)] text-[14px] font-normal"
          value={editor.formData.business_id}
          onChange={(e) => editor.updateField('business_id', e.target.value)}
        >
          <option value="">Select business</option>
          {businesses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </Label>
    )}
    {CREATE_SERVICE_TYPES.map((t) => {
      const TypeIcon = TYPE_ICONS[t.k];
      return (
        <section key={t.k} className="flex flex-col gap-2.5" aria-label={t.n}>
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 shrink-0 rounded-[12px] flex items-center justify-center" style={{ background: `color-mix(in srgb, ${brand} 10%, white)`, color: brand }}>
              <TypeIcon size={16} />
            </span>
            <div>
              <div className="text-[16px] font-bold" style={ink(900)}>{t.n}</div>
              <div className="text-[12px]" style={ink(500)}>{TYPE_SUB[t.k]}</div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {CREATE_SERVICE_SUBTYPES[t.k].map((s) => {
              const Icon = SUBTYPE_ICONS[s.k] || Box;
              return (
                <OfferPickerCard
                  key={s.k}
                  icon={<Icon size={16} />}
                  title={s.n}
                  description={s.d}
                  preset={s.preset.replace(/^Presets: /, '')}
                  onClick={() => flow.pickKind(t.k, s.k)}
                />
              );
            })}
          </div>
        </section>
      );
    })}
  </div>
);

// --- Step 2 · Details --------------------------------------------------------------------------

const TOOLS: Array<{ kind: MarkdownKind; label: string; aria: string }> = [
  { kind: 'bold', label: 'B', aria: 'Bold' },
  { kind: 'italic', label: 'I', aria: 'Italic' },
  { kind: 'heading', label: 'H2', aria: 'Heading' },
  { kind: 'bullet', label: '•', aria: 'Bulleted list' },
  { kind: 'numbered', label: '1.', aria: 'Numbered list' },
  { kind: 'link', label: '↗', aria: 'Link' },
];

const DetailsStep: React.FC<{ editor: Editor }> = ({ editor }) => {
  const { formData, updateField } = editor;
  const [lang, setLang] = useState<'en' | 'es'>('en');
  const area = useRef<HTMLTextAreaElement>(null);
  const nameKey = lang === 'en' ? 'name_en' : 'name_es';
  const descKey = lang === 'en' ? 'description_en' : 'description_es';
  const esEmpty = !formData.name_es.trim() && !formData.description_es.trim();
  const canCopy = esEmpty && (formData.name_en.trim() !== '' || formData.description_en.trim() !== '');

  const tool = (kind: MarkdownKind) => {
    const el = area.current;
    const value = formData[descKey];
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const edit = applyMarkdownEdit(value, start, end, kind, kind === 'link' ? window.prompt('Link URL', 'https://') ?? '' : undefined);
    if (!edit) return;
    updateField(descKey, edit.value);
    setTimeout(() => { el?.focus(); el?.setSelectionRange(edit.selStart, edit.selEnd); }, 0);
  };

  const copyFromEnglish = () => {
    if (!formData.name_es.trim()) updateField('name_es', formData.name_en);
    if (!formData.description_es.trim()) updateField('description_es', formData.description_en);
    setLang('es');
  };

  return (
    <div className="flex flex-col gap-5">
      <Card title="Name and description">
        <div className="flex" role="tablist" aria-label="Language" style={{ borderBottom: '1px solid var(--line)' }}>
          {([['en', 'English'], ['es', 'Español']] as const).map(([k, label]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={lang === k}
              onClick={() => setLang(k)}
              className="h-11 px-4 inline-flex items-center gap-1.5 bg-transparent border-0 text-[14px] font-semibold cursor-pointer"
              style={{ color: lang === k ? brand : 'var(--ink-500)', borderBottom: `2px solid ${lang === k ? brand : 'transparent'}` }}
            >
              {label}
              {k === 'es' && esEmpty && <span aria-label="empty" style={{ width: 7, height: 7, borderRadius: '50%', background: '#d98a00' }} />}
            </button>
          ))}
        </div>
        <Label text={lang === 'en' ? 'Name (English)' : 'Name (Español)'} required={lang === 'en'}>
          <input
            value={formData[nameKey]}
            onChange={(e) => updateField(nameKey, e.target.value)}
            className="h-11 px-3 rounded-[8px] border border-solid border-[var(--line)] bg-[var(--surface)] text-[14px] font-normal"
            placeholder={lang === 'en' ? 'Service name' : 'Nombre del servicio'}
          />
        </Label>
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-1.5 flex-wrap" role="toolbar" aria-label="Description formatting">
            {TOOLS.map((t) => (
              <button
                key={t.kind}
                type="button"
                aria-label={t.aria}
                onClick={() => tool(t.kind)}
                className="w-[34px] h-[30px] rounded-[6px] bg-white text-[13px] font-bold cursor-pointer"
                style={{ border: '1px solid var(--line)', ...ink(900) }}
              >
                {t.label}
              </button>
            ))}
            <span className="ml-auto text-[12px]" style={ink(500)}>Markdown</span>
          </div>
          <label className="flex flex-col gap-1.5 text-[13px] font-semibold" style={ink(700)}>
            Description
            <textarea
              ref={area}
              value={formData[descKey]}
              onChange={(e) => updateField(descKey, e.target.value)}
              rows={9}
              className="p-3 rounded-[8px] border border-solid border-[var(--line)] bg-[var(--surface)] text-[14px] font-normal leading-normal"
              style={{ minHeight: 240 }}
            />
          </label>
        </div>
        {lang === 'es' && canCopy && (
          <div className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-[10px] text-[13px] font-semibold" style={{ background: 'var(--warn-bg)', color: 'var(--warn-fg)' }}>
            <Sparkles size={16} aria-hidden />
            <span>Español is empty.</span>
            <button type="button" onClick={copyFromEnglish} className="bg-transparent border-0 p-0 underline font-semibold cursor-pointer" style={{ color: 'inherit' }}>
              Copy from English
            </button>
          </div>
        )}
      </Card>
    </div>
  );
};

// --- Step 3 · Look -----------------------------------------------------------------------------

const LookStep: React.FC<{ editor: Editor }> = ({ editor }) => {
  const { formData, updateField } = editor;
  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="m-0 text-[24px] font-bold" style={ink(900)}>How does it look in the app?</h1>
        <p className="m-0 mt-1.5 text-[15px]" style={ink(500)}>
          Optional. Skip it and add a photo later from the editor; the card uses the color meanwhile.
        </p>
      </div>
      <Card title="Look">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
          <div className="flex flex-col gap-1.5">
            <S3ImageUpload
              label="Cover photo"
              variant="slot"
              slotHeight="150px"
              placeholder="Upload photo · 16:9, JPG or PNG"
              value={formData.background_img_url}
              onChange={(key) => updateField('background_img_url', key)}
            />
            <span className="text-[12px]" style={ink(500)}>Shown at the top of the service in the app. Without one, the card color is used.</span>
          </div>
          <div className="flex flex-col gap-4">
            <BadgeField editor={editor} />
            <div className="flex flex-col gap-1.5">
              <span className="text-[13px] font-semibold" style={ink(700)}>Card color</span>
              <div className="flex gap-2.5 flex-wrap" role="radiogroup" aria-label="Card color">
                {CARD_COLORS.map((c) => {
                  const on = formData.background_color.toLowerCase() === c.toLowerCase();
                  return (
                    <button
                      key={c}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      aria-label={c}
                      onClick={() => updateField('background_color', c)}
                      className="w-8 h-8 rounded-full cursor-pointer border-0 p-0"
                      style={{ background: c, boxShadow: on ? `0 0 0 2px #fff, 0 0 0 4px ${brand}` : 'inset 0 0 0 1px rgba(0,0,0,.08)' }}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

// --- Step 4 · Booking & price ------------------------------------------------------------------

const BookingStep: React.FC<{ editor: Editor }> = ({ editor }) => {
  const { formData, updateField, setFormData } = editor;
  const free = isFreeForm(formData);
  const [changing, setChanging] = useState(false);
  const word = resourceWord(formData.subtype).singular;
  const times = sampleStartTimes(formData, 3);
  const currency = pricingCurrency(formData) === 'DOP' ? 'DOP' : 'USD';
  const stepMin = startStepMinutes(formData);
  const slots = hasSlots(formData);

  return (
    <div className="flex flex-col gap-5">
      <Card title="Booking">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {slots && (
            <UnitInput label="How long is a booking?" unit="minutes" required value={formData.interval_duration} onChange={(v) => updateField('interval_duration', v)} />
          )}
          <UnitInput label="Guests per booking" unit="people" value={formData.max_capacity} onChange={(v) => updateField('max_capacity', v)} />
          <UnitInput label="Book up to" unit="days ahead" value={formData.booking_window_end} onChange={(v) => updateField('booking_window_end', v)} />
          <UnitInput label="Book at least" unit="hours before" value={formData.booking_window_start} onChange={(v) => updateField('booking_window_start', v)} />
        </div>
        {hasStartTimes(formData) && (
          <div className="flex flex-col gap-3 rounded-[10px] p-3.5" style={{ background: 'var(--surface)', border: '1px solid var(--line)' }}>
            <div className="flex items-center justify-between gap-3">
              <span className="text-[14px] leading-snug" style={ink(900)}>
                Guests can start at <b>{times.join(' · ')}</b> …
              </span>
              <button type="button" onClick={() => setChanging((v) => !v)} className="bg-transparent border-0 p-0 text-[14px] font-semibold cursor-pointer whitespace-nowrap" style={{ color: brand }}>
                {changing ? 'Done' : 'Change'}
              </button>
            </div>
            {changing && (
              <div className="flex flex-col gap-2.5">
                <div className="text-[14px] font-semibold" style={ink(700)}>A guest can start a booking every</div>
                <div className="flex items-center gap-2.5 flex-wrap">
                  <span className="flex items-center gap-2 h-11 px-3 rounded-[8px] border border-solid border-[var(--line)] bg-white">
                    <input
                      inputMode="numeric"
                      aria-label="Minutes between start times"
                      value={formData.bookable_interval || String(stepMin)}
                      onChange={(e) => updateField('bookable_interval', e.target.value.replace(/[^\d]/g, ''))}
                      className="w-14 bg-transparent border-0 outline-none text-[14px]"
                    />
                    <span className="text-[14px] font-medium" style={ink(500)}>minutes</span>
                  </span>
                  {[15, 30, 60].map((m) => (
                    <SegButton key={m} on={stepMin === m && formData.bookable_interval !== ''} onClick={() => updateField('bookable_interval', String(m))}>{m} min</SegButton>
                  ))}
                </div>
                <span className="text-[12px]" style={ink(500)}>
                  Default is the booking length (one right after another). A smaller number lets guests start in between; bookings still can’t overlap the {word}.
                </span>
              </div>
            )}
          </div>
        )}
      </Card>

      <Card title="Price">
        <div className="flex items-center gap-3.5">
          <button
            type="button"
            role="switch"
            aria-checked={free}
            aria-label="Free to book"
            onClick={() => setFormData((prev) => setFree(prev, !isFreeForm(prev)))}
            className="relative w-[46px] h-[26px] rounded-full border-0 p-0 shrink-0 cursor-pointer"
            style={{ background: free ? brand : 'var(--line)' }}
          >
            <span className="absolute top-[3px] w-5 h-5 rounded-full bg-white" style={{ [free ? 'right' : 'left']: 3, boxShadow: '0 1px 3px rgba(0,0,0,.3)' } as React.CSSProperties} />
          </button>
          <div className="flex flex-col gap-0.5">
            <span className="text-[15px] font-bold" style={ink(900)}>Free to book</span>
            {free && <span className="text-[12px]" style={ink(500)}>Guests book without paying. Approval, refunds and prices don’t apply.</span>}
          </div>
        </div>
        {!free && !pricedByEventSize(formData) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <span className="text-[13px] font-semibold" style={ink(700)}>Currency</span>
              <div className="flex gap-2" role="group" aria-label="Currency">
                {(['DOP', 'USD'] as const).map((c) => (
                  <SegButton key={c} on={currency === c} onClick={() => setFormData((prev) => setCurrency(prev, c))}>{c}</SegButton>
                ))}
              </div>
            </div>
            <Label text={formData.price_unit === 'unit' ? `Price per ${word}` : 'Price per guest'} required>
              <span className="flex items-center gap-2 h-11 px-3 rounded-[8px] border border-solid border-[var(--line)] bg-[var(--surface)] font-normal">
                <input
                  inputMode="decimal"
                  aria-label="Price"
                  value={priceOf(formData)}
                  onChange={(e) => setFormData((prev) => setPrice(prev, e.target.value.replace(/[^\d.]/g, '')))}
                  className="grow min-w-0 bg-transparent border-0 outline-none text-[14px]"
                />
                <span className="shrink-0 text-[14px] font-medium" style={ink(500)}>{currency}</span>
              </span>
            </Label>
          </div>
        )}
        {!free && pricedByEventSize(formData) && (
          <p className="m-0 text-[13px]" style={ink(500)}>An event is priced by size. You set each size in the full editor after you create it.</p>
        )}
      </Card>

      {!free && (
        <Card title="Approval and cancellation">
          <div className="flex flex-col gap-1.5">
            <span className="text-[13px] font-semibold" style={ink(700)}>New bookings</span>
            <div className="flex gap-2 flex-wrap" role="group" aria-label="New bookings">
              <SegButton on={formData.approval_flow === 'instant'} onClick={() => updateField('approval_flow', 'instant')}>Confirmed instantly</SegButton>
              <SegButton on={formData.approval_flow === 'request'} onClick={() => updateField('approval_flow', 'request')}>You approve each one</SegButton>
            </div>
          </div>
          <div className="flex items-center gap-2.5 flex-wrap text-[14px]" style={ink(900)}>
            <span>Guests cancel free until</span>
            <input
              inputMode="numeric"
              aria-label="Hours before"
              value={formData.cancellation_time_limit}
              onChange={(e) => updateField('cancellation_time_limit', e.target.value.replace(/[^\d]/g, ''))}
              className="w-16 h-10 px-2.5 rounded-[8px] border border-solid border-[var(--line)] bg-[var(--surface)] text-[14px]"
            />
            <span>hours before. After that:</span>
            <select
              aria-label="After that"
              value={formData.cancellation_outside_window}
              onChange={(e) => updateField('cancellation_outside_window', e.target.value as typeof formData.cancellation_outside_window)}
              className="h-10 px-2.5 rounded-[8px] border border-solid border-[var(--line)] bg-[var(--surface)] text-[14px]"
            >
              <option value="none">No refund</option>
              <option value="auto">Refund automatically</option>
              <option value="request">You decide each time</option>
            </select>
          </div>
        </Card>
      )}
      <div className="px-4 py-3 rounded-[10px] text-[14px] leading-snug" style={{ background: `color-mix(in srgb, ${brand} 10%, white)`, color: 'var(--ink-900)' }}>
        Seasons, resources, add-ons and the rest are in the full editor after you create the service.
      </div>
    </div>
  );
};

// --- Shell -------------------------------------------------------------------------------------

export const NewServiceWizard: React.FC<{
  editor: Editor;
  flow: Flow;
  businessName?: string;
  businesses: Array<{ id: string | number; name?: string }>;
  canPickBusiness: boolean;
  onCreate: () => void;
  onCancel: () => void;
}> = ({ editor, flow, businessName, businesses, canPickBusiness, onCreate, onCancel }) => {
  const step = (flow.phase === 'form' ? 'kind' : flow.phase) as WizardStep;
  const idx = stepIndex(step);
  const meta = WIZARD_STEPS[idx];
  const free = isFreeForm(editor.formData);
  const blocker = stepBlocker(step, editor.formData, free);
  const last = step === 'booking';
  const title = `New service${businessName ? ` · ${businessName}` : ''}`;

  return (
    <div className="flex flex-col min-h-full" style={{ background: 'var(--surface)' }}>
      <header
        className="shrink-0 flex items-center justify-between gap-3 px-4 sm:px-6 py-3 bg-white flex-wrap"
        style={{ borderBottom: '1px solid var(--line)' }}
      >
        <div className="flex items-center gap-3.5 min-w-0">
          <button
            type="button"
            aria-label="Back"
            onClick={idx === 0 ? onCancel : flow.back}
            className="w-10 h-10 shrink-0 rounded-[10px] bg-white flex items-center justify-center cursor-pointer"
            style={{ border: '1px solid var(--line)', ...ink(900) }}
          >
            <ArrowLeft size={18} />
          </button>
          <div className="min-w-0">
            <div className="text-[18px] sm:text-[20px] font-bold truncate" style={ink(900)}>{title}</div>
            <div className="text-[13px]" style={ink(500)}>Step {idx + 1} of {WIZARD_STEPS.length} · {meta.title}</div>
          </div>
        </div>
        <ol aria-label="Steps" className="hidden md:flex gap-2 list-none m-0 p-0">
          {WIZARD_STEPS.map((s, i) => {
            const done = i < idx;
            const on = i === idx;
            return (
              <li key={s.key} aria-current={on ? 'step' : undefined} className="flex items-center gap-2 h-10 px-2 text-[13px] font-semibold" style={{ color: on ? 'var(--ink-900)' : done ? brand : 'var(--ink-500)' }}>
                <span
                  aria-hidden
                  className="inline-flex items-center justify-center rounded-full text-[12px]"
                  style={{
                    width: 24, height: 24,
                    background: on ? brand : done ? `color-mix(in srgb, ${brand} 10%, white)` : 'var(--line-subtle)',
                    color: on ? '#fff' : done ? brand : 'var(--ink-500)',
                  }}
                >
                  {done ? <Check size={12} strokeWidth={3} /> : i + 1}
                </span>
                {s.label}
              </li>
            );
          })}
        </ol>
        <button type="button" onClick={onCancel} className="btn ghost shrink-0">Cancel</button>
      </header>

      <main className="grow flex justify-center gap-8 px-4 sm:px-6 pt-6 sm:pt-8 pb-12">
        <div className={`w-full ${step === 'kind' ? 'max-w-[960px]' : 'max-w-[720px]'} min-w-0`}>
          {step === 'kind' && <KindStep editor={editor} flow={flow} businesses={businesses} canPickBusiness={canPickBusiness} />}
          {step === 'details' && (
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2 flex-wrap text-[13px] font-semibold">
                {businessName && <span className="px-2.5 py-1 rounded-[8px]" style={{ background: 'var(--line-subtle)', ...ink(700) }}>{businessName}</span>}
                {flow.pickedLabel && (
                  <span className="px-2.5 py-1 rounded-[8px]" style={{ background: `color-mix(in srgb, ${brand} 10%, white)`, color: brand }}>
                    {CREATE_SERVICE_TYPES.find((t) => t.k === flow.createType)?.n} · {flow.pickedLabel}
                  </span>
                )}
                <button type="button" onClick={() => flow.go('kind')} className="bg-transparent border-0 p-0 underline font-semibold cursor-pointer" style={{ color: brand }}>Change</button>
              </div>
              <DetailsStep editor={editor} />
            </div>
          )}
          {step === 'look' && <LookStep editor={editor} />}
          {step === 'booking' && <BookingStep editor={editor} />}
        </div>
        {(step === 'look' || step === 'booking') && (
          <aside className="hidden lg:flex w-[300px] shrink-0 flex-col gap-2.5" aria-label="Preview">
            <ServiceLivePreview formData={editor.formData} />
            <div className="text-[12px] text-center" style={ink(500)}>Preview only. The price is always calculated when the guest books.</div>
          </aside>
        )}
      </main>

      {step !== 'kind' && (
        <footer className="sticky bottom-0 mt-auto flex items-center justify-between gap-3 px-4 sm:px-6 py-3.5 bg-white flex-wrap" style={{ borderTop: '1px solid var(--line)' }}>
          <button type="button" className="btn" onClick={flow.back} disabled={editor.isSaving}>Back</button>
          <div className="flex items-center gap-3 flex-wrap justify-end">
            {editor.saveError && <span role="alert" data-testid="save-error" className="text-xs" style={{ color: 'var(--danger, #c0392b)' }}>Could not save: {editor.saveError}</span>}
            {!editor.saveError && blocker && <span className="text-xs" style={ink(500)}>{blocker}</span>}
            {step === 'look' && <button type="button" className="btn" onClick={flow.next}>Skip for now</button>}
            {last ? (
              <button type="button" className="btn primary" onClick={onCreate} disabled={!!blocker || !editor.isFormValid || editor.isSaving}>
                {editor.isSaving ? 'Creating…' : 'Create service'}
              </button>
            ) : (
              <button type="button" className="btn primary" onClick={flow.next} disabled={!!blocker}>Continue</button>
            )}
          </div>
        </footer>
      )}
    </div>
  );
};
